import React, { useState, useEffect, useRef } from 'react';
import Image from 'next/image';
import ReactMapGL, {
  Marker,
  WebMercatorViewport,
  NavigationControl,
} from 'react-map-gl';
import token from '../../tokens';
import { isEmpty } from 'lodash';
import * as turf from '@turf/turf';

const latitudeAstoriaWest = 40.769555;
const longitudeAstoriaWest = -73.934048;
const markerWidth = 22;
const markerHeight = 26;
const transitionDuration = 1000;
const markerIcon = `M11,0 C4.9258,0 0,4.76125 0,10.6329167 C0,16.5966667 4.8125,20.67325 11,26 C17.1875,20.67325 22,16.5966667 22,10.6329167 C22,4.76125 17.0742,0 11,0 Z`;

interface MapboxMapProps {
  data: Array<Filter>;
  activeFilter: string;
  setSelectedPin: Function;
  selectedPin?: Pin;
}

interface Filter {
  title: string;
  id: string;
  data: Array<Pin>;
}

interface Pin {
  name: any;
  address: any;
  geopoint: {
    latitude: number;
    longitude: number;
  };
}

interface Viewport {
  width?: number;
  height?: number;
  latitude: number;
  longitude: number;
  zoom?: number;
  bearing?: number;
  pitch?: number;
  transitionDuration?: number;
}

export const MapBox: React.FunctionComponent<MapboxMapProps> = ({
  data,
  activeFilter,
  selectedPin,
  setSelectedPin,
}) => {
  const mapContainer = useRef<HTMLDivElement>(null);
  const [ready, setReady] = useState<boolean>(false);
  const [pinData, setPinData] = useState<Pin[]>([]);
  const [viewport, setViewport] = useState<Viewport>({
    width: 100,
    height: 100,
    latitude: 40.7679585,
    longitude: -73.9253534,
    zoom: 14.2,
    bearing: 0,
    pitch: 0,
    transitionDuration: transitionDuration,
  });

  const plants = [
    {
      iconSize: [100, 100],
      coordinates: [-73.964811, 40.776814], // left (central park)
    },
    {
      iconSize: [70, 70],
      coordinates: [-73.943387, 40.774779], // middle
    },
    {
      iconSize: [80, 80],
      coordinates: [-73.940867, 40.76615], // right
    },
    {
      iconSize: [50, 50],
      coordinates: [-73.936894, 40.768364], // close to astoria west
    },
    {
      iconSize: [80, 80],
      coordinates: [-73.922578, 40.778761], // top-right (astoria park)
    },
  ];

  // all ferry icons have the same size
  const ferries = [
    {
      coordinates: [-73.935709, 40.771786], // astoria
    },
    {
      coordinates: [-73.952694, 40.756702], // roosevelt
    },
    {
      coordinates: [-73.957704, 40.748495], // long island city
    },
    {
      coordinates: [-73.94206, 40.777307], // manhattan
    },
    {
      coordinates: [-73.96367031968407, 40.731355778444396], // greenpoint
    },
    {
      coordinates: [-73.9707808708058, 40.74354081768235], // East 34th Street
    },
    {
      coordinates: [-73.96462416986819, 40.72080269844818], // North Williamsburg
    },
    {
      coordinates: [-73.97282693088083, 40.73191903492243], // Stuyvesant Cove
    },
    {
      coordinates: [-73.97923092637184, 40.7099921054729], // Corlears Hook
    },
    {
      coordinates: [-73.97023275843011, 40.70857430744518], // South Williamsburg
    },
    {
      coordinates: [-73.99571751084265, 40.70357152260382], // Dumbo / Brooklyn Bridge
    },
    {
      coordinates: [-74.00552462221546, 40.70243453307554], // Wallstreet
    },
  ];

  function handlePinClick(pin: Pin) {
    setSelectedPin(pin);
  }

  useEffect(() => {
    let didUnmount = false;
    let timeout: NodeJS.Timeout;

    if (!didUnmount) {
      timeout = setTimeout(() => setReady(true), 1000);
    }

    return () => {
      clearInterval(timeout);
      didUnmount = true;
    };
  }, []);

  useEffect(() => {
    let didUnmount = false;

    if (!didUnmount) {
      const pins = data.filter((entry) => entry.id === activeFilter);

      if (pins[0] && pins[0].data) {
        setPinData(pins[0].data);
      }
    }

    return () => {
      didUnmount = true;
    };
  }, [activeFilter, data]);

  // selectedPin changes
  useEffect(() => {
    if (!pinData) {
      return;
    }
    let didUnmount = false;

    if (!didUnmount && mapContainer.current && ready && !isEmpty(pinData)) {
      // generate data for turf/bbox
      const markers = [];
      pinData.map((pin) => {
        if (pin.geopoint && pin.geopoint.longitude && pin.geopoint.latitude) {
          markers.push([pin.geopoint.longitude, pin.geopoint.latitude]);
        }
      });

      // add Astoria West coordinates,
      // since there are not part of the pins data
      markers.push([longitudeAstoriaWest, latitudeAstoriaWest]);

      if (markers.length > 1) {
        // get bounding box
        const line = turf.lineString(markers);
        const [minLng, minLat, maxLng, maxLat] = turf.bbox(line);

        const width = mapContainer.current.clientWidth;
        const height = mapContainer.current.clientHeight;

        // define new viewport
        const vp = new WebMercatorViewport({
          ...viewport,
          width: width,
          height: height,
        });

        const { longitude, latitude, zoom } = vp.fitBounds(
          [
            [minLng, minLat],
            [maxLng, maxLat],
          ],
          {
            padding: {
              // multiply value should not go above 0.45
              // in order to have the padding lower than the width/height.
              // The app will break otherwise!
              top: height * 0.15,
              bottom: height * 0.15,
              left: width * 0.15,
              right: width * 0.15,
            },
          }
        );

        // update viewport
        setViewport({
          ...viewport,
          width: width,
          height: height,
          longitude,
          latitude,
          zoom,
          transitionDuration: transitionDuration,
        });
      }
    }

    return () => {
      didUnmount = true;
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pinData, ready]);

  return (
    <div ref={mapContainer} className="mapbox__map">
      {selectedPin && (
        <a
          href={`https://maps.google.com/?q=${selectedPin.geopoint.latitude},${selectedPin.geopoint.longitude}`}
          target="_blank"
          rel="noreferrer"
          className="mapbox__selected-pin"
        >
          <p className="mapbox__selected-pin__title">
            {selectedPin.name[0].text}
          </p>
          <p>{selectedPin.address[0]?.text || '-'}</p>
          <p className="mapbox__selected-pin__link-text">Open in Google Maps</p>
        </a>
      )}

      {ready && (
        <ReactMapGL
          {...viewport}
          width="100%"
          height="100%"
          mapboxApiAccessToken={token.MapBoxToken}
          mapStyle="mapbox://styles/tobi-lauer/ckre0njln307q17o5m47pct01"
          onViewportChange={
            // @ts-ignore
            (nextViewport) => setViewport(nextViewport)
          }
          scrollZoom={false}
          touchZoom={true}
          // dragPan={ false }
          touchAction="pan-y"
        >
          <NavigationControl className="mapbox__controls-wrapper" />

          {/* Plants */}
          {plants.map((plant, key) => (
            <Marker
              key={`plant-` + key}
              longitude={plant.coordinates[0]}
              latitude={plant.coordinates[1]}
              offsetLeft={-plant.iconSize[0] / 2}
              offsetTop={-plant.iconSize[1] / 2}
            >
              <Image
                src="/map-plant.png"
                alt="plant"
                width={plant.iconSize[0]}
                height={plant.iconSize[1]}
              />
            </Marker>
          ))}

          {/* Ferry Stops */}
          {ferries.map((ferry, key) => (
            <Marker
              key={`ferry-` + key}
              longitude={ferry.coordinates[0]}
              latitude={ferry.coordinates[1]}
              offsetLeft={-20 / 2}
              offsetTop={-26 / 2}
            >
              <Image src="/map-ferry.png" alt="ferry" width={20} height={26} />
            </Marker>
          ))}

          {/* Pins */}
          {pinData &&
            pinData.map((pin, index) => {
              // only render Marker if longitude & latitude exists
              if (
                pin.geopoint &&
                pin.geopoint.longitude &&
                pin.geopoint.latitude
              ) {
                return (
                  <Marker
                    key={`marker-${index}`}
                    longitude={pin.geopoint.longitude}
                    latitude={pin.geopoint.latitude}
                    onClick={() => handlePinClick(pin)}
                    offsetLeft={-markerWidth / 2}
                    offsetTop={-markerHeight}
                    className={
                      pin === selectedPin
                        ? 'mapbox__marker mapbox__marker--active'
                        : 'mapbox__marker'
                    }
                  >
                    {/* Marker SVG */}
                    <svg
                      height={`${markerHeight}px`}
                      width={`${markerWidth}px`}
                      viewBox={`0 0 ${markerWidth} ${markerHeight}`}
                      className="mapbox__marker__svg"
                      style={{
                        cursor: 'pointer',
                        fill: '#000',
                        stroke: 'none',
                        transformOrigin: 'center bottom',
                        transform: pin === selectedPin ? 'scale(1.6)' : 'none',
                      }}
                    >
                      <path d={markerIcon} fillRule="nonzero" />
                    </svg>

                    {/* Text/Number overlaying on Marker SVG */}
                    <div
                      className="mapbox__marker__number"
                      style={{
                        cursor: 'pointer',
                        color: '#fff',
                        position: 'absolute',
                        textAlign: 'center',
                        height: `${markerHeight}px`,
                        width: `${markerWidth}px`,
                        fontSize: '12px',
                        lineHeight: `${markerHeight - 2}px`,
                        fontWeight: 'bold',
                        transformOrigin: 'center bottom',
                        transform:
                          pin === selectedPin
                            ? 'scale(1.3) translateY(-4px)'
                            : 'none',
                      }}
                    >
                      {index + 1}
                    </div>
                  </Marker>
                );
              }
            })}

          {/* Astoria Pin is always visible  */}
          <Marker
            key={`marker-astoria-west`}
            latitude={latitudeAstoriaWest}
            longitude={longitudeAstoriaWest}
            offsetLeft={-markerWidth / 2}
            offsetTop={-markerHeight}
          >
            {/* Marker SVG */}
            <svg
              height={markerHeight}
              width={markerWidth}
              viewBox={`0 0 ${markerWidth} ${markerHeight}`}
              style={{
                cursor: 'pointer',
                fill: '#000',
                stroke: 'none',
                transformOrigin: 'center bottom',
                transform: 'scale(2.4)',
              }}
            >
              <path d={markerIcon} />
            </svg>

            {/* Logo overlaying on Marker SVG */}
            <div
              style={{
                position: 'absolute',
                textAlign: 'center',
                height: `${markerHeight}px`,
                width: `${markerWidth}px`,
                top: 0,
                transform: 'scale(1.8) translateY(-4px)',
                transformOrigin: 'center bottom',
              }}
            >
              <Image
                src="/logo-white.svg"
                alt="astoria west logo"
                width="130"
                height="52"
              />
            </div>
          </Marker>
        </ReactMapGL>
      )}
    </div>
  );
};
